import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useViewport } from '../hooks/useViewport';
import { dialogCardStyle } from './ui/dialogCard';
import { ConnectorPairing, connectorAPI, myRunnerKeyAPI, cloudRunnerAPI } from '../api/client';
import { apiErrorMessage } from '../api/errors';

interface RunnerConnectPromptProps {
  orgId: string;
  onClose: () => void;
  // What triggered the prompt, e.g. "Your agent run is queued".
  reason?: string;
}

type ConnectorOS = 'windows' | 'linux' | 'darwin';

const detectOS = (): ConnectorOS => {
  const ua = navigator.userAgent;
  if (/Mac/i.test(ua)) return 'darwin';
  if (/Linux|X11/i.test(ua) && !/Android/i.test(ua)) return 'linux';
  return 'windows';
};

const OS_LABEL: Record<ConnectorOS, string> = {
  windows: 'Windows',
  linux: 'Linux',
  darwin: 'macOS',
};

// Shown when an agent run can't find an active runner. Tries to open the
// locally installed OpenV Agent Connector via its openv-connector:// link;
// the browser gives no signal when the handler isn't registered, so if no
// runner comes online in time we fall back: auto-download the connector for
// first-time setups (no runner key yet), or hint that it may not be installed
// on this machine. Polls the member's runner status until it comes online.
export const RunnerConnectPrompt: React.FC<RunnerConnectPromptProps> = ({
  orgId,
  onClose,
  reason,
}) => {
  const { isPhone } = useViewport();
  const [phase, setPhase] = useState<'opening' | 'waiting' | 'connected'>('opening');
  // Transient runners give a no-install way out of this prompt: lease one and
  // the queued run has somewhere to go without downloading anything. null
  // until the first read; false on deployments with no runner pool.
  const [cloudEnabled, setCloudEnabled] = useState<boolean | null>(null);
  const [cloudBusy, setCloudBusy] = useState(false);
  const [cloudStarted, setCloudStarted] = useState(false);
  const [pairing, setPairing] = useState<ConnectorPairing | null>(null);
  const [download, setDownload] = useState<'idle' | 'checking' | 'started' | 'unavailable'>('idle');
  const [error, setError] = useState('');
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  // null until the first runner-key fetch resolves; then whether a key exists.
  const hasKeyRef = useRef<boolean | null>(null);
  const autoDownloadedRef = useRef(false);

  const os = detectOS();
  const osLabel = OS_LABEL[os];
  const binName = os === 'windows' ? 'openv-connector.exe' : './openv-connector';

  // Fire the protocol link through a hidden iframe: no navigation, and
  // browsers without a handler fail silently instead of showing an error page.
  const openConnector = useCallback((link: string) => {
    if (frameRef.current) {
      frameRef.current.src = link;
    } else {
      window.location.href = link;
    }
  }, []);

  // Preflight the bundle, then trigger the browser download. Keeps a missing
  // bundle (dist not built on the server) as an inline message, not a 404 page.
  const startDownload = useCallback(async () => {
    setDownload('checking');
    if (!(await connectorAPI.downloadAvailable(os))) {
      setDownload('unavailable');
      return;
    }
    try {
      // Remembered so a later "no key in THIS workspace" doesn't re-download
      // a connector this browser already fetched (pairing is per-workspace).
      localStorage.setItem('openv-connector-downloaded', '1');
    } catch {
      /* private mode etc. — auto-download gating just degrades */
    }
    const a = document.createElement('a');
    a.href = connectorAPI.downloadURL(os);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setDownload('started');
  }, [os]);

  // Open-or-pair in one step: mint a one-time code and hand it to the
  // connector, which only spends it when it has no pairing for this
  // workspace. Falls back to a plain start link if minting fails.
  const openOrPair = useCallback(async () => {
    setError('');
    try {
      const res = await connectorAPI.createPairing(orgId);
      setPairing(res.data);
      openConnector(res.data.open_link || res.data.deep_link);
    } catch (err: any) {
      setError(
        `Couldn't create a pairing code (${apiErrorMessage(err)}) — trying to open an already-paired connector instead.`
      );
      openConnector(connectorAPI.startLink(orgId));
    }
  }, [orgId, openConnector]);

  // Does this deployment offer cloud runners at all?
  useEffect(() => {
    let cancelled = false;
    cloudRunnerAPI
      .get(orgId)
      .then((res) => {
        if (cancelled) return;
        setCloudEnabled(res.data.enabled);
        if (res.data.session) setCloudStarted(true);
      })
      .catch(() => {
        if (!cancelled) setCloudEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Lease a cloud runner instead of installing anything. The member still has
  // to sign their agents into it — that is what the settings panel is for —
  // but the runner itself is online in seconds.
  const startCloudRunner = useCallback(async () => {
    setCloudBusy(true);
    setError('');
    try {
      await cloudRunnerAPI.start(orgId);
      setCloudStarted(true);
    } catch (err: any) {
      setError(`Couldn't start a cloud runner: ${apiErrorMessage(err)}`);
    } finally {
      setCloudBusy(false);
    }
  }, [orgId]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      openOrPair();
    }, 300);
    const fallback = window.setTimeout(
      () => setPhase((p) => (p === 'opening' ? 'waiting' : p)),
      3000
    );
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(fallback);
    };
  }, [openOrPair]);

  // Poll until this member's personal runner shows up online.
  useEffect(() => {
    if (phase === 'connected') return;
    const poll = window.setInterval(async () => {
      try {
        const res = await myRunnerKeyAPI.get(orgId);
        hasKeyRef.current = !!res.data.key_record;
        if (res.data.online) setPhase('connected');
      } catch {
        // Transient — keep polling.
      }
      if (!cloudStarted) return;
      try {
        // A leased cloud runner counts as connected once its node has
        // actually picked the lease up.
        const cloud = await cloudRunnerAPI.get(orgId);
        if (cloud.data.session?.status === 'active') setPhase('connected');
      } catch {
        // Transient — keep polling.
      }
    }, 3000);
    return () => window.clearInterval(poll);
  }, [orgId, phase, cloudStarted]);

  // If the protocol launch produced no runner, the connector likely isn't
  // installed. With no runner key yet (first-time setup) start the download
  // automatically — unless this browser has downloaded the connector before:
  // runner keys are per-workspace, so "no key in this workspace" often just
  // means "pair the existing connector with this workspace", not "install".
  useEffect(() => {
    if (phase !== 'waiting' || autoDownloadedRef.current) return;
    const t = window.setTimeout(() => {
      if (autoDownloadedRef.current || hasKeyRef.current !== false) return;
      let downloadedBefore = false;
      try {
        downloadedBefore = !!localStorage.getItem('openv-connector-downloaded');
      } catch {
        /* unreadable storage — treat as first time */
      }
      if (downloadedBefore) return;
      autoDownloadedRef.current = true;
      startDownload();
    }, 4000);
    return () => window.clearTimeout(t);
  }, [phase, startDownload]);


  const waitingStatus = () => {
    switch (download) {
      case 'started':
        return (
          <>
            The connector didn&apos;t respond, so the download has started — check your browser
            downloads for <code>{binName.replace('./', '')}</code>. Move it somewhere permanent, run{' '}
            <code>{binName}</code> once to register it, then click “Open connector” below.
          </>
        );
      case 'checking':
        return <>Checking for a connector download…</>;
      default:
        return (
          <>
            Waiting for a runner to come online. If nothing happened, the connector probably
            isn&apos;t installed on this machine yet — use the options below. Already installed?
            “Open connector” starts it and pairs this workspace automatically if needed. If its
            window reports that it doesn&apos;t understand the link, that copy predates this
            feature: download it again below and run it once to update the link.
          </>
        );
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <iframe ref={frameRef} title="openv-connector" style={{ display: 'none' }} />
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ ...dialogCardStyle(isPhone, 620), background: 'var(--surface)' }}
      >
        <h3 style={{ marginTop: 0, color: 'var(--text)' }}>
          {phase === 'connected' ? 'Runner connected' : 'No runner is online'}
        </h3>

        {error && (
          <div
            style={{
              background: 'var(--tint-red)',
              border: '1px solid var(--danger)',
              color: 'var(--danger-strong)',
              padding: '10px 14px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {phase === 'connected' ? (
          <>
            <p style={{ fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>
              ✓ A runner of yours is online — queued runs will start now.
            </p>
            <div style={{ textAlign: 'right' }}>
              <button className="button" style={{ width: 'auto' }} onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>
              {reason || 'This run needs a runner.'} Agents execute on your own machine with your
              own AI subscription via the OpenV Agent Connector.
            </p>

            <div
              style={{
                background: 'var(--surface-alt)',
                border: '1px solid var(--border-soft)',
                borderRadius: 4,
                padding: '10px 14px',
                fontSize: 13,
                color: 'var(--text)',
                marginBottom: 14,
              }}
            >
              {phase === 'opening' ? (
                <>Opening your OpenV Agent Connector… allow the browser prompt if one appears.</>
              ) : (
                waitingStatus()
              )}
            </div>

            {download === 'unavailable' && (
              <div
                style={{
                  background: 'var(--tint-red)',
                  border: '1px solid var(--danger)',
                  color: 'var(--danger-strong)',
                  padding: '10px 14px',
                  borderRadius: 4,
                  marginBottom: 14,
                  fontSize: 13,
                }}
              >
                No connector bundle for {osLabel} is available on this server. An operator needs to
                build the download bundles with <code>make connector-dist</code>.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <button className="button" style={{ width: 'auto' }} onClick={openOrPair}>
                Open connector
              </button>
              <button
                className="button-secondary button"
                style={{ width: 'auto' }}
                onClick={startDownload}
                disabled={download === 'checking'}
              >
                {download === 'checking' ? 'Checking…' : `Download for ${osLabel}`}
              </button>
              {cloudEnabled && !cloudStarted && (
                <button
                  className="button-secondary button"
                  style={{ width: 'auto' }}
                  onClick={startCloudRunner}
                  disabled={cloudBusy}
                  title="Run in the cloud instead — nothing to install"
                >
                  {cloudBusy ? 'Starting…' : 'Start a cloud runner'}
                </button>
              )}
            </div>

            {cloudStarted && (
              <div
                style={{
                  background: 'var(--tint-blue)',
                  border: '1px solid var(--accent)',
                  borderRadius: 4,
                  padding: '12px 14px',
                  fontSize: 13,
                  color: 'var(--text)',
                  marginBottom: 16,
                }}
              >
                A cloud runner is starting for you. It has no agent sign-ins yet — open your
                settings → <b>Agent sign-ins</b> and connect a provider, and it will pick this run
                up. The runner ends on its own once you stop using it, and its sign-ins end with
                it.
              </div>
            )}

            {pairing && (
              <div
                style={{
                  background: 'var(--tint-blue)',
                  border: '1px solid var(--accent)',
                  borderRadius: 4,
                  padding: '12px 14px',
                  fontSize: 13,
                  color: 'var(--text)',
                  marginBottom: 16,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Pairing started</div>
                <div style={{ marginBottom: 8 }}>
                  Your connector should open and pair automatically. If not, run it manually with
                  this one-time code (valid until{' '}
                  {new Date(pairing.expires_at).toLocaleTimeString()}):
                </div>
                <pre
                  style={{
                    background: 'var(--code-block-bg)',
                    color: 'var(--code-block-text)',
                    borderRadius: 4,
                    padding: '8px 12px',
                    fontSize: 12,
                    overflowX: 'auto',
                    margin: 0,
                  }}
                >
                  {`${binName} "${pairing.open_link || pairing.deep_link}"`}
                </pre>
              </div>
            )}

            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              First time? Download the connector — a single <code>{binName.replace('./', '')}</code>{' '}
              — move it somewhere permanent and double-click it once to register it, then click
              “Open connector” here: it pairs this workspace automatically. Your CLI sign-ins never
              leave your machine.
            </div>

            <div style={{ textAlign: 'right' }}>
              <button className="button-secondary button" style={{ width: 'auto' }} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
